import Image from "next/image";
import type { SiteTemplateKey } from "@/lib/siteTemplates";
import { getTemplateStyles } from "@/lib/siteTemplateStyles";

type LogoItem = {
  url?: string;
  altText?: string;
  linkUrl?: string;
};

export function LogoStripBlock({
  content,
  template = "classic",
}: {
  content: Record<string, unknown>;
  template?: SiteTemplateKey;
}) {
  const heading = String(content.heading ?? "");
  const logos = (Array.isArray(content.logos) ? content.logos : []) as LogoItem[];
  const styles = getTemplateStyles(template);

  if (logos.length === 0 && !heading) return null;

  const Logo = ({ logo }: { logo: LogoItem }) => {
    if (!logo.url) return null;
    const image = (
      <Image
        src={logo.url}
        alt={logo.altText || ""}
        width={120}
        height={60}
        className="h-10 w-auto max-w-[8rem] object-contain opacity-80 grayscale transition-opacity hover:opacity-100 hover:grayscale-0"
      />
    );
    return logo.linkUrl ? (
      <a href={logo.linkUrl} className="inline-flex items-center justify-center p-2" target="_blank" rel="noopener noreferrer">
        {image}
      </a>
    ) : (
      <span className="inline-flex items-center justify-center p-2">{image}</span>
    );
  };

  return (
    <section className={styles.textGroup.section}>
      {heading && (
        <p className="mb-[calc(var(--space-unit)*1)] text-center text-sm font-medium uppercase tracking-wider text-[var(--color-secondary)]">
          {heading}
        </p>
      )}
      <ul className="flex flex-wrap items-center justify-center gap-[calc(var(--space-unit)*1)]">
        {logos.map((logo, index) =>
          logo.url ? (
            <li key={index}>
              <Logo logo={logo} />
            </li>
          ) : null,
        )}
      </ul>
    </section>
  );
}
